import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../constants/site";

export const LeadModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setStatus("idle");
      setForm({ name: "", email: "", company: "" });
    }, 300);
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-cream px-4 py-3.5 text-sm text-walnut outline-none transition-colors duration-300 placeholder:text-clay focus:border-terracotta";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-walnut/60 p-4 backdrop-blur-sm"
          onClick={close}
          data-testid="lead-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-paper p-9 shadow-[0_40px_90px_rgba(42,32,28,0.4)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-modal"
          >
            <button
              data-testid="lead-modal-close"
              onClick={close}
              aria-label="Close"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-line text-umber transition-colors duration-300 hover:bg-walnut hover:text-paper"
            >
              <X className="h-4 w-4" />
            </button>

            {status === "success" ? (
              <div className="py-8 text-center" data-testid="lead-success-message">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage text-paper">
                  <Check className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-serif text-3xl font-medium tracking-tight text-walnut">
                  You&rsquo;re on the list.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-umber/85">
                  We&rsquo;ll reply within 1 business day.
                </p>
              </div>
            ) : (
              <>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
                  Free sample recap
                </p>
                <h3 className="mt-3 font-serif text-3xl font-medium tracking-tight text-walnut">
                  Tell us about your next all-hands.
                </h3>
                <form onSubmit={submit} className="mt-7 space-y-4" data-testid="lead-form">
                  <input
                    data-testid="lead-name-input"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    data-testid="lead-email-input"
                    required
                    type="email"
                    placeholder="Work email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    data-testid="lead-company-input"
                    required
                    placeholder="Company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className={inputCls}
                  />
                  {status === "error" && (
                    <p className="text-sm text-terracotta" data-testid="lead-error-message">
                      Something went wrong — please try again.
                    </p>
                  )}
                  <button
                    data-testid="lead-submit-button"
                    type="submit"
                    disabled={status === "loading"}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-terracotta py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terracotta-dark disabled:opacity-60"
                  >
                    {status === "loading" ? "Sending…" : "Request a Sample Recap"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <p className="text-center text-xs leading-relaxed text-clay">
                    By submitting you agree to our{" "}
                    <a href="#/privacy" className="text-terracotta underline underline-offset-2" data-testid="lead-privacy-link">
                      Privacy Policy
                    </a>
                    .
                  </p>
                  <p className="text-center text-xs leading-relaxed text-clay">
                    Please don&rsquo;t paste confidential documents here. We&rsquo;ll
                    share a secure way to send materials after we reply.
                  </p>
                </form>
                <div className="mt-7 rounded-2xl border border-line bg-cream/60 p-5" data-testid="what-happens-next">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">
                    What happens next
                  </p>
                  <ol className="mt-3 list-none space-y-2.5 text-sm leading-relaxed text-umber/90">
                    <li>1. You send us your name, email and company.</li>
                    <li>
                      2. We reply within 1 business day to confirm scope and how
                      to share your all-hands materials securely.
                    </li>
                    <li>
                      3. We draft your recap email, Slack highlights and decisions
                      list. You review and approve before anything is used.
                    </li>
                  </ol>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
